import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const LOWER_BETTER = new Set(["too_usd", "material_cost_usd", "total_losses_kw", "no_load_loss_w", "load_loss_w", "total_weight_kg", "hot_spot_c"]);

function DiffRow({ k, va, vb }) {
  const same = String(va) === String(vb);
  const numA = typeof va === "number" ? va : parseFloat(va);
  const numB = typeof vb === "number" ? vb : parseFloat(vb);
  let color = "var(--c-muted)", arrow = "";
  if (!same && Number.isFinite(numA) && Number.isFinite(numB)) {
    const up = numB > numA;
    arrow = up ? "↑" : "↓";
    color = LOWER_BETTER.has(k) ? (up ? "var(--c-red)" : "var(--c-green)") : "var(--c-accent)";
  }
  return (
    <tr className="border-b border-[var(--c-border)]/50 last:border-0" data-testid={`diff-row-${k}`}>
      <td className="px-3 py-2 font-mono text-[var(--c-muted)]">{k}</td>
      <td className="px-3 py-2 font-mono text-[var(--c-text)]">{typeof va === "object" && va ? `${va.l}×${va.w}×${va.h}` : String(va ?? "—")}</td>
      <td className="px-3 py-2 font-mono" style={{ color: same ? "var(--c-muted)" : color }}>
        {typeof vb === "object" && vb ? `${vb.l}×${vb.w}×${vb.h}` : String(vb ?? "—")} {arrow}
      </td>
    </tr>
  );
}

export default function CompareVersions({ versions, onClose }) {
  const sorted = [...(versions || [])].sort((x, y) => (x.version || 0) - (y.version || 0));
  const [a, setA] = useState(sorted[0]?.id || "");
  const [b, setB] = useState(sorted[sorted.length - 1]?.id || "");
  const va = sorted.find((v) => v.id === a);
  const vb = sorted.find((v) => v.id === b);

  const inputKeys = [...new Set([...Object.keys(va?.inputs || {}), ...Object.keys(vb?.inputs || {})])];
  const metricKeys = [...new Set([...Object.keys(va?.results_summary || {}), ...Object.keys(vb?.results_summary || {})])];

  const picker = (val, set, testid) => (
    <Select value={val} onValueChange={set}>
      <SelectTrigger data-testid={testid} className="h-8 bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] text-xs">
        <SelectValue placeholder="Pick version…" />
      </SelectTrigger>
      <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
        {sorted.map((v) => (
          <SelectItem key={v.id} value={v.id} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)] text-xs">
            v{v.version ?? "—"} — {v.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="compare-modal">
        <DialogHeader>
          <DialogTitle className="text-[var(--c-text)] text-sm">Compare design versions</DialogTitle>
          <DialogDescription className="sr-only">Side-by-side diff of two saved design versions</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {picker(a, setA, "compare-a")}
          {picker(b, setB, "compare-b")}
        </div>
        {va && vb && (
          <div className="border border-[var(--c-border)] rounded-sm overflow-hidden" data-testid="compare-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[var(--c-border)] bg-[var(--c-bg)]">
                  <th className="text-left px-3 py-2 text-[9px] font-mono tracking-widest uppercase text-[var(--c-muted)]">Parameter</th>
                  <th className="text-left px-3 py-2 text-[9px] font-mono tracking-widest uppercase text-[var(--c-muted)]">v{va.version ?? "?"}</th>
                  <th className="text-left px-3 py-2 text-[9px] font-mono tracking-widest uppercase text-[var(--c-muted)]">v{vb.version ?? "?"}</th>
                </tr>
              </thead>
              <tbody>
                <tr><td colSpan={3} className="px-3 py-1.5 text-[9px] font-mono tracking-widest uppercase text-[var(--c-accent)] bg-[var(--c-bg)]/60">Inputs</td></tr>
                {inputKeys.map((k) => <DiffRow key={k} k={k} va={va.inputs?.[k]} vb={vb.inputs?.[k]} />)}
                <tr><td colSpan={3} className="px-3 py-1.5 text-[9px] font-mono tracking-widest uppercase text-[var(--c-accent)] bg-[var(--c-bg)]/60">Metrics</td></tr>
                {metricKeys.map((k) => <DiffRow key={k} k={k} va={va.results_summary?.[k]} vb={vb.results_summary?.[k]} />)}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex gap-3 font-mono text-[9px] text-[var(--c-muted)]">
          <span><span className="text-[var(--c-green)]">green</span> improved</span>
          <span><span className="text-[var(--c-red)]">red</span> degraded</span>
          <span><span className="text-[var(--c-accent)]">blue</span> changed (neutral)</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
